import { go } from '../../App';
import { usePolled } from '../../data';
import { PageHead, Panel, Stars, StatusChip, MoneyText, formatDay } from '../../ui';
import { Booking, ReviewAggregate } from '../../types';

// The hotel's Supplier-profile Record (canon 09 — one of the five hotel Record
// screens), the drill-in from the supplier directory (H4). It shows the
// supplier's detail, the rating and the reviews behind it, and the history of
// jobs with THIS hotel. No new endpoint: the header + rating come from the
// directory the hotel already reads (GET /suppliers), and "history with you" is
// the hotel's OWN bookings (GET /bookings) filtered to this supplier — the same
// own-partition projection as Guests/Reports. A hotel never reads a supplier's
// bookings (DECISION-020); it reads its own, which is where the shared jobs live.

interface DirRow {
  supplierAccountId?: string;
  name: string;
  status: string;
  email?: string;
  phone?: string;
  rating?: ReviewAggregate;
}

function statusLabel(s: string): string {
  return s === 'active'
    ? 'Active supplier'
    : s === 'requested'
      ? 'Connection requested'
      : s === 'invited'
        ? 'Invited'
        : s;
}

export function SupplierProfile({ accountId }: { accountId?: string }) {
  const dir = usePolled<{ items: DirRow[] }>('/suppliers');
  const bookings = usePolled<{ items: Booking[] }>('/bookings');

  const row = (dir.data?.items ?? []).find((s) => s.supplierAccountId === accountId);
  const history = (bookings.data?.items ?? [])
    .filter((b) => b.supplierAccountId === accountId)
    .sort((a, b) => Date.parse(b.start) - Date.parse(a.start));

  const name = row?.name ?? history[0]?.supplierName ?? 'Supplier';
  const rating = row?.rating;

  // A garbage or not-connected id renders an honest empty state, never a wrong
  // URL under a real screen (the directory drill is the only way in).
  if (dir.data && !row && bookings.data && history.length === 0) {
    return (
      <div>
        <PageHead title="Supplier" sub="" />
        <div className="empty-neutral">
          This supplier isn&rsquo;t in your directory. <a href="#/suppliers">Back to suppliers</a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHead title={name} sub={row ? statusLabel(row.status) : 'Your history together'}>
        <a
          href="#/suppliers"
          className="btn secondary"
          style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
        >
          Back to suppliers
        </a>
      </PageHead>

      <Panel>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
          <div>
            {row?.email && <div className="hint">{row.email}</div>}
            {row?.phone && <div className="hint">{row.phone}</div>}
            {!row?.email && !row?.phone && <div className="hint">Your shared jobs are below.</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            {rating && rating.count > 0 ? (
              <>
                <div>
                  <Stars rating={rating.average} /> <strong>{rating.average.toFixed(1)}</strong>
                </div>
                <div className="hint">
                  {rating.count} review{rating.count === 1 ? '' : 's'} from you
                </div>
              </>
            ) : (
              <div className="hint">No reviews yet</div>
            )}
          </div>
        </div>
      </Panel>

      <div style={{ fontWeight: 600, margin: 'var(--space-lg) 0 var(--space-sm)' }}>History with you</div>
      {bookings.data && history.length === 0 ? (
        <div className="empty-neutral">No bookings with {name} yet.</div>
      ) : (
        <Panel flush>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Experience</th>
                <th>Status</th>
                <th>Value</th>
                <th>Your review</th>
              </tr>
            </thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => go(`/bookings/${b.id}`)}>
                  <td className="mono">{formatDay(b.start)}</td>
                  <td>{b.productName ?? '—'}</td>
                  <td>
                    <StatusChip status={b.status} />
                  </td>
                  <td>
                    <MoneyText money={b.guestAmount ?? b.supplierAmount} />
                  </td>
                  <td>
                    {b.review ? (
                      <span>
                        <Stars rating={b.review.rating} /> {b.review.rating}/5
                      </span>
                    ) : b.status === 'completed' ? (
                      <span className="hint">Not rated</span>
                    ) : (
                      <span className="hint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
